import PropTypes from 'prop-types';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { SingleBinIcon, fillableStackHeightPx } from '../../atoms/BinIcons';

import './MultiBinVisual.scss';

/** Must match {@link SINGLE_BIN_ICON_HEIGHT_SCALE_MIN} on `SingleBinIcon`. */
const MIN_BIN_ICON_HEIGHT_SCALE = 0.22;

/**
 * Vertical scale for bin SVG (1 = largest bin in set). Width stays fixed in CSS.
 *
 * @param {number} capacityGrams
 * @param {number} maxCapacityGrams
 * @returns {number}
 */
export function binIconHeightScaleFromRelativeCapacity(capacityGrams, maxCapacityGrams) {
  if (!maxCapacityGrams || maxCapacityGrams <= 0) return 1;
  const c = Math.max(0, capacityGrams || 0);
  return Math.max(MIN_BIN_ICON_HEIGHT_SCALE, Math.min(1, c / maxCapacityGrams));
}

function alignPerBinFillPercentsLinearFixedCone(
  perBinFillPercents,
  bins,
  maxCapacityGrams,
  heightScaleForCapacity,
) {
  const H_max = fillableStackHeightPx(1);
  if (H_max <= 0) return perBinFillPercents;

  return perBinFillPercents.map((pStd, i) => {
    const cap = bins[i]?.capacityInGrams || 0;
    if (cap <= 0) return pStd;
    const H_i = fillableStackHeightPx(heightScaleForCapacity(cap));
    if (H_i <= 0) return pStd;
    return pStd * (cap / maxCapacityGrams) * (H_max / H_i);
  });
}

/**
 * Multi-bin icon fill: same inventory mass lines up across bins. For exactly two bins with
 * different capacities, use a step in m at the smaller bin’s capacity.
 * For 3+ bins, falls back to a linear fixed-cone rescale.
 *
 * @param {number[]|null|undefined} perBinFillPercents
 * @param {Array<{ capacityInGrams?: number }>} bins
 * @param {number} maxCapacityGrams
 * @param {(capacityGrams: number) => number} heightScaleForCapacity
 * @returns {number[]|null|undefined}
 */
export function alignPerBinFillPercentsForFixedCone(
  perBinFillPercents,
  bins,
  maxCapacityGrams,
  heightScaleForCapacity,
) {
  if (!perBinFillPercents || bins.length < 2 || !maxCapacityGrams) {
    return perBinFillPercents;
  }

  if (bins.length !== 2) {
    return alignPerBinFillPercentsLinearFixedCone(
      perBinFillPercents,
      bins,
      maxCapacityGrams,
      heightScaleForCapacity,
    );
  }

  const c0 = bins[0]?.capacityInGrams || 0;
  const c1 = bins[1]?.capacityInGrams || 0;
  if (c0 <= 0 || c1 <= 0) {
    return alignPerBinFillPercentsLinearFixedCone(
      perBinFillPercents,
      bins,
      maxCapacityGrams,
      heightScaleForCapacity,
    );
  }

  const C_s = Math.min(c0, c1);
  const C_l = Math.max(c0, c1);
  if (C_l - C_s <= 0) {
    return perBinFillPercents;
  }

  const H_s = fillableStackHeightPx(heightScaleForCapacity(C_s));
  const H_l = fillableStackHeightPx(heightScaleForCapacity(C_l));
  if (H_s <= 0 || H_l <= 0) {
    return alignPerBinFillPercentsLinearFixedCone(
      perBinFillPercents,
      bins,
      maxCapacityGrams,
      heightScaleForCapacity,
    );
  }

  const iSmall = c0 <= c1 ? 0 : 1;

  return perBinFillPercents.map((pStd, i) => {
    const cap = bins[i]?.capacityInGrams || 0;
    if (cap <= 0) return pStd;
    const m = (pStd / 100) * cap;
    const H_i = fillableStackHeightPx(heightScaleForCapacity(cap));

    let dPx;
    if (i === iSmall) {
      dPx = m * (H_s / C_s);
    } else if (m <= C_s) {
      dPx = m * (H_s / C_s);
    } else {
      dPx = H_s + (m - C_s) * ((H_l - H_s) / (C_l - C_s));
    }

    return Math.min(100, Math.max(0, (100 * dPx) / H_i));
  });
}

/**
 * Fillable SVG stack height in px (body + cone, lid excluded) for a bin at `capacityGrams` vs `maxCapacityGrams`.
 *
 * @param {number} capacityGrams
 * @param {number} maxCapacityGrams
 * @returns {number}
 */
export function binFillableStackHeightPx(capacityGrams, maxCapacityGrams) {
  const scale = binIconHeightScaleFromRelativeCapacity(capacityGrams, maxCapacityGrams);
  return fillableStackHeightPx(scale);
}

/**
 * @param {Array<{ capacityInGrams?: number, levelGrams?: number | null }>} bins
 * @param {boolean} inventoryHidden
 */
function computeVisualMetrics(bins, inventoryHidden) {
  const maxBinCapacityGrams =
    bins.length <= 1 ? 0 : Math.max(0, ...bins.map((b) => b.capacityInGrams || 0));

  const heightScaleForCapacity = (capacityGrams) =>
    bins.length <= 1 ? 1 : binIconHeightScaleFromRelativeCapacity(capacityGrams, maxBinCapacityGrams);

  const rawFillPercents = bins.map((bin) => {
    if (inventoryHidden) return 0;
    const cap = bin.capacityInGrams || 0;
    const g = bin.levelGrams;
    if (cap <= 0) return 0;
    if (g == null || !Number.isFinite(g)) return 0;
    return Math.min(100, Math.max(0, (100 * g) / cap));
  });

  let fillPercentsForIcons = rawFillPercents;
  if (!inventoryHidden && bins.length >= 2 && maxBinCapacityGrams > 0) {
    const aligned = alignPerBinFillPercentsForFixedCone(
      rawFillPercents,
      bins,
      maxBinCapacityGrams,
      heightScaleForCapacity,
    );
    if (aligned) fillPercentsForIcons = aligned;
  }

  return { fillPercentsForIcons, heightScaleForCapacity };
}

/**
 * Shared bin silhouettes. Each bin should include `levelGrams` (resolved inventory mass).
 * Computes fill %, fixed-cone alignment, and stack height scale / px via {@link binFillableStackHeightPx}.
 */
function MultiBinVisual({ bins = [], iconFillColour, inventoryHidden = false }) {
  const { fillPercentsForIcons, heightScaleForCapacity } = useMemo(
    () => computeVisualMetrics(bins, inventoryHidden),
    [bins, inventoryHidden],
  );

  const rowOuterRef = useRef(null);
  const rowInnerRef = useRef(null);
  const [rowScale, setRowScale] = useState(1);
  const [rowHeight, setRowHeight] = useState(null);

  useLayoutEffect(() => {
    if (bins.length < 2) {
      setRowScale(1);
      setRowHeight(null);
      return undefined;
    }

    const outer = rowOuterRef.current;
    const inner = rowInnerRef.current;
    if (!outer || !inner) return undefined;

    const updateRowScale = () => {
      const availableWidth = outer.clientWidth;
      const naturalWidth = inner.offsetWidth;
      const naturalHeight = inner.offsetHeight;

      if (availableWidth <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
        return;
      }

      const scale = Math.min(1, availableWidth / naturalWidth);
      setRowScale(scale);
      setRowHeight(naturalHeight * scale);
    };

    updateRowScale();

    const observer = new ResizeObserver(updateRowScale);
    observer.observe(outer);
    observer.observe(inner);

    return () => observer.disconnect();
  }, [bins.length, fillPercentsForIcons]);

  return (
    <>
      {bins.length === 1 && (
        <SingleBinIcon
          className="MultiBinVisual-binSetIcon"
          fillPercent={fillPercentsForIcons[0] ?? 0}
          fillColour={iconFillColour}
        />
      )}
      {bins.length >= 2 && (
        <div
          ref={rowOuterRef}
          className="MultiBinVisual-binIconRowOuter"
          style={rowHeight != null ? { height: rowHeight } : undefined}
        >
          <div
            ref={rowInnerRef}
            className="MultiBinVisual-binIconRow"
            style={{ transform: `scale(${rowScale})` }}
          >
            {bins.map((bin, index) => (
              <SingleBinIcon
                key={bin.id || String(index)}
                className="MultiBinVisual-binSetIcon MultiBinVisual-binSetIcon--multi"
                fillPercent={fillPercentsForIcons[index] ?? 0}
                fillColour={iconFillColour}
                heightScale={heightScaleForCapacity(bin.capacityInGrams)}
              />
            ))}
          </div>
        </div>
      )}
      {bins.length === 0 && (
        <SingleBinIcon className="MultiBinVisual-binSetIcon" fillPercent={0} fillColour={iconFillColour} />
      )}
    </>
  );
}

MultiBinVisual.propTypes = {
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      capacityInGrams: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  iconFillColour: PropTypes.string.isRequired,
  inventoryHidden: PropTypes.bool,
};

export default MultiBinVisual;
